import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import Header from '../components/Header';
import Hero from '../components/Hero';
import Decarbonizing from '../components/Decarbonizing';
import GreenSolutions from '../components/GreenSolutions';
import CorporateStory from '../components/CorporateStory';
import CardSlider from '../components/CardSlider';
import TechDevelopments from '../components/TechDevelopments';
import Footer from '../components/Footer';
import ContactModal from '../components/ContactModal';
import {
  HAPPENING_NEWS as MOCK_HAPPENING,
  NEWS_MEDIA as MOCK_MEDIA,
  COMMUNITY_CONNECT as MOCK_COMMUNITY,
  HERO_SLIDES as MOCK_HERO,
  STATS as MOCK_STATS,
  PROJECT_LOCATIONS as MOCK_PROJECTS,
  TECH_VIDEOS as MOCK_TECH,
} from '../mock/mock';
import api from '../api/client';

const Home = () => {
  const [filmOpen, setFilmOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const { toast } = useToast();

  const [heroSlides, setHeroSlides] = useState(MOCK_HERO);
  const [stats, setStats] = useState(MOCK_STATS);
  const [projects, setProjects] = useState(MOCK_PROJECTS);
  const [happening, setHappening] = useState(MOCK_HAPPENING);
  const [media, setMedia] = useState(MOCK_MEDIA);
  const [community, setCommunity] = useState(MOCK_COMMUNITY);
  const [techVideos, setTechVideos] = useState(MOCK_TECH);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [h, s, p, nH, nM, c, t] = await Promise.all([
          api.getHeroSlides().catch(() => null),
          api.getStats().catch(() => null),
          api.getProjects().catch(() => null),
          api.getNews('happening').catch(() => null),
          api.getNews('media').catch(() => null),
          api.getCommunity().catch(() => null),
          api.getTechVideos().catch(() => null),
        ]);
        if (cancelled) return;
        if (h && h.length) setHeroSlides(h);
        if (s && s.length) setStats(s);
        if (p && p.length) setProjects(p);
        if (nH && nH.length) setHappening(nH);
        if (nM && nM.length) setMedia(nM);
        if (c && c.length) setCommunity(c);
        if (t && t.length) setTechVideos(t);
      } catch (e) {
        console.error('Failed to load site content', e);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleNewsletter = async (email) => {
    try {
      await api.subscribeNewsletter(email);
      toast({ title: 'Subscribed!', description: 'Thanks for subscribing to our newsletter.' });
      return true;
    } catch (e) {
      const msg = e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || 'Please try again.';
      toast({ title: 'Subscription failed', description: String(msg), variant: 'destructive' });
      return false;
    }
  };

  const handleContact = async (payload) => {
    try {
      await api.submitContact(payload);
      toast({ title: 'Message sent!', description: 'We will get back to you soon.' });
      return true;
    } catch (e) {
      const msg = e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || 'Please try again.';
      toast({ title: 'Submission failed', description: String(msg), variant: 'destructive' });
      return false;
    }
  };

  return (
    <div className="bg-white text-[#1a1a1a] min-h-screen">
      <Header onContact={() => setContactOpen(true)} />
      <Hero slides={heroSlides} onPlayFilm={() => setFilmOpen(true)} />
      <Decarbonizing />
      <GreenSolutions stats={stats} projects={projects} />
      <CorporateStory />
      <CardSlider
        eyebrow="NEWSROOM"
        title={<>What&rsquo;s Happening <span className="text-[#8ec045]">at Raynav</span></>}
        items={happening}
      />
      <CardSlider
        eyebrow="NEWS &amp; MEDIA"
        title="News &amp; Media"
        subtitle="Get the latest news to stay updated with the recent developments"
        items={media}
      />
      <CardSlider
        eyebrow="CSR"
        title={<>Community <span className="text-[#8ec045]">Connect</span></>}
        subtitle="Our projects exhibit our dedication to sustainable change"
        items={community}
      />
      <TechDevelopments videos={techVideos} />
      <Footer onNewsletter={handleNewsletter} onContact={() => setContactOpen(true)} />

      <ContactModal open={contactOpen} onClose={() => setContactOpen(false)} onSubmit={handleContact} />

      {filmOpen && (
        <div
          className="fixed inset-0 z-[90] bg-black/95 flex items-center justify-center p-4"
          onClick={() => setFilmOpen(false)}
        >
          <button className="absolute top-6 right-6 text-white" onClick={() => setFilmOpen(false)}>
            <X className="w-8 h-8" />
          </button>
          <div className="w-full max-w-5xl aspect-video" onClick={(e) => e.stopPropagation()}>
            <iframe
              width="100%"
              height="100%"
              src="https://www.youtube.com/embed/BHACKCNDMW8?autoplay=1"
              title="Brand Film"
              frameBorder="0"
              allow="autoplay; encrypted-media"
              allowFullScreen
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
